import React, { useMemo } from 'react';

import { Box, Stack, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';

import { useScrapeSessionContext } from '../../PricingToolSessionContext';
import { audMoney } from '../../constants';
import { computeDailyDiscountStats } from '../scrape/otaParity/computeGenuineDiscount';
import { computeDailyMarketStats } from '../scrape/otaParity/computeOtaMarketStats';
import { computeOtaSummaryStats, mean } from '../scrape/otaParity/computeSummaryStats';

import { CELL_SX, HEADER_SX } from './packageCategories';

const HIGHLIGHT_SX = { ...CELL_SX, bgcolor: '#ffff00', fontWeight: 600 };

const COL_WIDTHS = [175, 65, 85, 110, 110, 110, 110];

export default function PackageOptionsTable() {
  const { stayInputs, parsedCsv } = useScrapeSessionContext();

  const avgTotalOtaRate = useMemo(() => {
    if (!parsedCsv) {
      return null;
    }
    const allQuotes = parsedCsv.rows.flatMap((row) =>
      Object.values(row.otaRates).filter((v): v is number => v != null),
    );
    return mean(allQuotes);
  }, [parsedCsv]);
  const avgNightlyOtaRate = avgTotalOtaRate != null && stayInputs?.nights ? avgTotalOtaRate / stayInputs.nights : null;

  const summaryStats = useMemo(() => {
    if (!parsedCsv || !stayInputs) {
      return null;
    }
    const marketStats = computeDailyMarketStats(parsedCsv, undefined, stayInputs.blackoutDates);
    const discountStats = computeDailyDiscountStats(marketStats, stayInputs);
    return computeOtaSummaryStats(parsedCsv, marketStats, discountStats, stayInputs.nights);
  }, [parsedCsv, stayInputs]);
  const avgGenuineDiscountPct = summaryStats?.weightedGenDiscountPct.valuePct ?? null;
  const avgSellUnderMinOtaPct = summaryStats?.weightedSellVsMinOtaPct.valuePct ?? null;
  const genDiscountQuartiles = summaryStats?.genDiscountQuartiles ?? null;
  const sellVsMinOtaQuartiles = summaryStats?.sellVsMinOtaQuartiles ?? null;
  const lowestOtaQuote = summaryStats?.lowestOtaQuote ?? null;
  const highestOtaQuote = summaryStats?.highestOtaQuote ?? null;

  const fmtPct = (v: number | null) => (v != null ? `${v.toFixed(2)}%` : '');
  const fmtRate = (v: number | null) => (v != null ? audMoney(v) : '');

  return (
    <Box sx={{ p: 2 }}>
      <Stack sx={{ width: 'fit-content' }}>
        <TableContainer sx={{ width: 'auto', overflowX: 'visible' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', width: 'auto' }}>
            <colgroup>
              {COL_WIDTHS.map((width, i) => (
                <col key={i} style={{ width }} />
              ))}
            </colgroup>
            <TableBody>
              <TableRow>
                <TableCell sx={HEADER_SX}>Nights in Scrape</TableCell>
                <TableCell align="center" sx={HIGHLIGHT_SX}>
                  {parsedCsv ? stayInputs?.nights : ''}
                </TableCell>
                <TableCell sx={HEADER_SX}>Currency</TableCell>
                <TableCell align="center" sx={HIGHLIGHT_SX}>
                  {parsedCsv ? stayInputs?.currencyCode : ''}
                </TableCell>
                <TableCell align="center" colSpan={3} sx={HEADER_SX}>
                  Distribution of Discount
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} sx={HEADER_SX}>
                  Avg Total/Nightly OTA Rate
                </TableCell>
                <TableCell align="center" sx={CELL_SX}>
                  {fmtRate(avgTotalOtaRate)}
                </TableCell>
                <TableCell align="center" sx={CELL_SX}>
                  {fmtRate(avgNightlyOtaRate)}
                </TableCell>
                <TableCell align="center" sx={HEADER_SX}>
                  1 QTR
                </TableCell>
                <TableCell align="center" sx={HEADER_SX}>
                  Median
                </TableCell>
                <TableCell align="center" sx={HEADER_SX}>
                  3 QTR
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} sx={HEADER_SX}>
                  Average Genuine Discount
                </TableCell>
                <TableCell colSpan={2} sx={CELL_SX}>
                  {fmtPct(avgGenuineDiscountPct)}
                </TableCell>
                <TableCell align="center" sx={CELL_SX}>
                  {fmtPct(genDiscountQuartiles?.q1 ?? null)}
                </TableCell>
                <TableCell align="center" sx={CELL_SX}>
                  {fmtPct(genDiscountQuartiles?.median ?? null)}
                </TableCell>
                <TableCell align="center" sx={CELL_SX}>
                  {fmtPct(genDiscountQuartiles?.q3 ?? null)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} sx={HEADER_SX}>
                  Average SELL under MIN OTA
                </TableCell>
                <TableCell colSpan={2} sx={CELL_SX}>
                  {fmtPct(avgSellUnderMinOtaPct)}
                </TableCell>
                <TableCell align="center" sx={CELL_SX}>
                  {fmtPct(sellVsMinOtaQuartiles?.q1 ?? null)}
                </TableCell>
                <TableCell align="center" sx={CELL_SX}>
                  {fmtPct(sellVsMinOtaQuartiles?.median ?? null)}
                </TableCell>
                <TableCell align="center" sx={CELL_SX}>
                  {fmtPct(sellVsMinOtaQuartiles?.q3 ?? null)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Box mt={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <TableContainer sx={{ width: 'auto', overflowX: 'visible' }}>
            <Table size="small" sx={{ tableLayout: 'fixed', width: 330 }}>
              <colgroup>
                <col style={{ width: 110 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 110 }} />
              </colgroup>
              <TableBody>
                <TableRow>
                  <TableCell sx={CELL_SX} />
                  <TableCell align="center" sx={HEADER_SX}>
                    Rate
                  </TableCell>
                  <TableCell align="center" sx={HEADER_SX}>
                    Count
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={HEADER_SX}>Lowest</TableCell>
                  <TableCell align="center" sx={CELL_SX}>
                    {fmtRate(lowestOtaQuote?.value ?? null)}
                  </TableCell>
                  <TableCell align="center" sx={CELL_SX}>
                    {lowestOtaQuote?.count ?? ''}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={HEADER_SX}>Highest</TableCell>
                  <TableCell align="center" sx={CELL_SX}>
                    {fmtRate(highestOtaQuote?.value ?? null)}
                  </TableCell>
                  <TableCell align="center" sx={CELL_SX}>
                    {highestOtaQuote?.count ?? ''}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
    </Box>
  );
}
